import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  SignatureEditForm,
  SignatureEditInitialValues,
  SignatureComponents,
  SignatureAdditionalParams,
  SignatureFormOptions,
  onSignatureFormSaved,
  ISignatureFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedSignature, ISharedPatchSignature, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchSignature, patchSignature, getSignatureUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedSignature> {}

const SignatureFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchSignature(id).then(signature => setValue(signature));
  }, [id, setValue, ]);

  const param_props: ISignatureFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && SignatureEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && SignatureFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchSignature, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchSignature) => value && save(() => patchSignature(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onSignatureFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.Signature, value)}
    >
      <AtlasForm<ISharedPatchSignature>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={SignatureComponents}
        additionalParams={SignatureAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={SignatureEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('SIGNATURE')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const SignatureFormEditView = React.memo(withEdit(SignatureFormEdit, getSignatureUpdateSchema));
